/**
 * Sortable Exercise Card
 * Wrapper around ExerciseCard that adds drag and drop functionality
 */

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Plus, Minus, Trash2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useSplitConfigStore, type SplitExercise, type SetType } from '../../stores/splitConfigStore'
import { useUserSettings } from '../../api/hooks/useSettings'
import { convertWeight, convertWeightToKg, getWeightUnit } from '../../lib/unit-utils'
import { SetTypeBadge } from './SetTypeBadge'
import { UnifiedButton } from '../ui/unified-button'

interface SortableExerciseCardProps {
  exercise: SplitExercise
  routineId: string
}

export const SortableExerciseCard = ({ exercise, routineId }: SortableExerciseCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: exercise.id })

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  }

  const { 
    updateSetInExercise,
    addSetToExercise,
    removeSetFromExercise,
    removeExerciseFromRoutine
  } = useSplitConfigStore()
  
  const { data: userSettings } = useUserSettings()
  const unitSystem = userSettings?.settings?.units || 'metric'
  const weightUnit = getWeightUnit(unitSystem)

  const handleWeightChange = (setId: string, weight: string) => {
    const weightNum = weight === '' ? null : parseFloat(weight)
    if (!isNaN(weightNum as number) || weight === '') {
      // Convert from user's unit to kg for storage
      const weightInKg = weightNum !== null ? convertWeightToKg(weightNum, unitSystem) : null
      updateSetInExercise(routineId, exercise.id, setId, { 
        weight: weightInKg 
      })
    }
  }

  const handleRepsChange = (setId: string, reps: string) => {
    const repsNum = reps === '' ? 0 : parseInt(reps)
    if (!isNaN(repsNum) || reps === '') {
      updateSetInExercise(routineId, exercise.id, setId, { 
        reps: repsNum 
      })
    }
  }

  const handleSetTypeChange = (setId: string, type: SetType) => {
    updateSetInExercise(routineId, exercise.id, setId, { type })
  }

  const handleAddSet = () => {
    addSetToExercise(routineId, exercise.id)
  }

  const handleRemoveSet = (setId: string) => {
    // If this is the last set, remove the entire exercise
    if (exercise.sets.length === 1) {
      removeExerciseFromRoutine(routineId, exercise.id)
    } else {
      removeSetFromExercise(routineId, exercise.id, setId)
    }
  }

  const handleRemoveExercise = () => {
    removeExerciseFromRoutine(routineId, exercise.id)
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'py-3',
        isDragging && 'opacity-50 z-50'
      )}
    >
      {/* Exercise header */}
      <div className="mb-3 flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-medium truncate">{exercise.name}</h4>
        </div>
        
        <div className="flex items-center gap-1">
          <button
            onClick={handleRemoveExercise}
            className="flex h-6 w-6 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
            title="Remove exercise"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
          
          {/* Drag handle */}
          <div
            {...attributes}
            {...listeners}
            className={cn(
              'flex h-6 w-6 items-center justify-center cursor-grab rounded',
              'hover:bg-muted/50 transition-colors',
              isDragging && 'cursor-grabbing'
            )}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </div>

      
      {/* Sets */}
      <div className="space-y-1">
        {exercise.sets.map((set, index) => {
          // Calculate the normal set number (only counting normal sets)
          const normalSetNumber = exercise.sets
            .slice(0, index + 1)
            .filter(s => s.type === 'normal')
            .length
            
          return (
            <div key={set.id} className={`flex items-center justify-between px-2 py-1 rounded ${index % 2 === 0 ? 'bg-muted/30' : ''}`}>
              {/* Set type badge */}
              <SetTypeBadge
                type={set.type}
                setNumber={index + 1}
                normalSetNumber={normalSetNumber}
                onChange={(type) => handleSetTypeChange(set.id, type)}
              />

            {/* Inputs grouped on the right */}
            <div className="flex items-center gap-2">
              {/* Weight input */}
              <div className="relative">
                <input
                  type="number"
                  value={set.weight !== null ? parseFloat(convertWeight(set.weight, unitSystem).toFixed(1)) : ''}
                  onChange={(e) => handleWeightChange(set.id, e.target.value)}
                  placeholder="0"
                  className="h-8 w-24 bg-transparent border-none outline-none text-center text-base pr-6 focus:bg-muted/50 rounded transition-colors [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  step="0.5"
                  min="0"
                />
                <span className="absolute right-1 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  {weightUnit}
                </span>
              </div>

              {/* × symbol */}
              <span className="text-muted-foreground text-xs">×</span>

              {/* Reps input */}
              <input
                type="number"
                value={set.reps || ''}
                onChange={(e) => handleRepsChange(set.id, e.target.value)}
                placeholder="0"
                className="h-8 w-12 bg-transparent border-none outline-none text-center text-base focus:bg-muted/50 rounded transition-colors [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                min="1"
              />

              {/* Delete set button */}
              <button
                onClick={() => handleRemoveSet(set.id)}
                className="ml-2 flex h-6 w-6 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                title={exercise.sets.length === 1 ? "Remove exercise" : "Remove set"}
              >
                <Minus className="h-3 w-3" />
              </button>
            </div>
          </div>
          )
        })}
      </div>

          {/* Add set button */}
          <div className="mt-2">
        <UnifiedButton
          variant="ghost"
          size="sm"
          onClick={handleAddSet}
          className="h-8 w-full border border-dashed border-border/50 text-xs text-muted-foreground hover:text-foreground"
        >
          <Plus className="mr-1 h-3 w-3" />
          Add Set
        </UnifiedButton>
      </div>
    </div>
  )
}